import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateEngageMessageById } from 'api/engage';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import * as Form from 'components/form';
import { UseGivebutterFormReturn } from 'components/form/useGivebutterForm';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateToServerDateTime } from 'helpers/dates';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

const useDraftAutoSave = (formMethods?: UseGivebutterFormReturn<LetterDraftFormValues>) => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: draft } = useCurrentLetter();
    const possibleFormMethods = Form.useGivebutterFormContext(true);
    const methods = formMethods ?? possibleFormMethods;
    const { getValues, reset, setError, clearErrors } = methods;

    return useMutation({
        mutationFn: () => {
            const formValues = getValues();

            const mailingsSettings = draft.settings.send_to_print_and_mail_service
                ? {
                      postage_type: formValues.letterSettings.postageType.value,
                      is_double_sided: formValues.letterSettings.isDoubleSided,
                      exclude_contacts_missing_merge_fields:
                          formValues.excludeContactsMissingMergeFields,
                  }
                : {};

            return updateEngageMessageById(accountId, draft.id, {
                channel: 'letter',
                internal_name: formValues.name,
                sending_at:
                    formValues.processingSpeed === 'scheduled' && formValues.shippingDate
                        ? dateToServerDateTime(formValues.shippingDate)
                        : null,
                sender: formValues.returnAddress
                    ? { id: Number(formValues.returnAddress), table: 'addresses' }
                    : undefined,
                settings: {
                    is_address_required: !formValues.includeContactsWithNoAddress,
                    generate_letter_for: formValues.generateFor,
                    ...mailingsSettings,
                },
            });
        },
        onSuccess: data => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.setQueryData(accountQueryKeys.engageMessageById(accountId, draft.id), data);
            reset(getValues());
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            clearErrors();
            if (typeof errors === 'object') {
                for (let field in errors) {
                    const key = fieldMap[field] || field;
                    setError(key, {
                        message: errors[field][0],
                        type: 'server',
                    });
                }
            }
        },
    });
};

const fieldMap: {
    [key: string]: keyof LetterDraftFormValues;
} = {
    internal_name: 'name',
    sending_at: 'shippingDate',
    return_address_name: 'returnAddress',
    sender: 'returnAddress',
};

export { useDraftAutoSave };
